<script setup lang="ts">
import { useUserStore } from '~/store/user'
import type { MwBffAccountSelectionResponse, MwBffSessionResponse } from '~/types/mw-bff'

/**
 * The emailed 6-digit second factor, as its own step.
 *
 * Shared by /login and /register because a NEW account can be asked for one
 * too: WHMCS answers `ValidateLogin` for a freshly created client with
 * `twoFactorEnabled`, so the sign-in that follows a signup hits the same
 * challenge an ordinary login does. When this lived only in login.vue the
 * register page had no branch for it and dropped the customer on /login with
 * nothing carried over — a brand-new account, one step from being signed in,
 * told its own login had failed.
 *
 * Owns the code entry, the resend budget and the verification call; hands the
 * finished session (or a still-pending account choice) back to the page.
 */
const props = defineProps<{
  /** Shown in the description so the user knows where the code went. */
  email: string
  challengeId: string
  rememberMe?: boolean
  /** Set when the caller already knows which client account to land on. */
  clientId?: number | null
  /** Label for the secondary button (defaults to the shared "back"). */
  backLabel?: string
}>()

const emit = defineEmits<{
  verified: [session: MwBffSessionResponse]
  accountSelection: [selection: MwBffAccountSelectionResponse]
  back: []
  error: [message: string]
}>()

const { t: $t } = useI18n()
const toast = useToast()
const userStore = useUserStore()

const isLoading = ref(false)
const otpDigits = ref<string[]>(['', '', '', '', '', ''])
const otpInputs = ref<(HTMLInputElement | null)[]>([])
const code = computed(() => otpDigits.value.join(''))

const resendCountdown = ref(0)
const resendCount = ref(0)
const resendMaxed = ref(false)
const isResending = ref(false)
let resendTimer: ReturnType<typeof setInterval> | null = null

function setOtpRef(el: unknown, i: number) {
  otpInputs.value[i] = el as HTMLInputElement | null
}

function onOtpInput(event: Event, index: number) {
  const input = event.target as HTMLInputElement
  const digit = input.value.replace(/[^0-9]/g, '').slice(-1)
  otpDigits.value[index] = digit
  if (digit && index < 5) {
    nextTick(() => otpInputs.value[index + 1]?.focus())
  }
}

function onOtpKeydown(event: KeyboardEvent, index: number) {
  if (event.key === 'Backspace') {
    if (otpDigits.value[index]) {
      otpDigits.value[index] = ''
    }
    else if (index > 0) {
      otpDigits.value[index - 1] = ''
      nextTick(() => otpInputs.value[index - 1]?.focus())
    }
  }
  else if (event.key === 'ArrowLeft' && index > 0) {
    otpInputs.value[index - 1]?.focus()
  }
  else if (event.key === 'ArrowRight' && index < 5) {
    otpInputs.value[index + 1]?.focus()
  }
  else if (event.key === 'Enter' && code.value.length === 6) {
    // A 6-digit code and a keyboard: Enter has to finish the login, the same
    // way it finishes every other step of these forms.
    event.preventDefault()
    void submit()
  }
}

function onOtpPaste(event: ClipboardEvent) {
  event.preventDefault()
  const text = event.clipboardData?.getData('text') ?? ''
  const digits = text.replace(/[^0-9]/g, '').slice(0, 6).split('')
  digits.forEach((d, i) => { if (i < 6) otpDigits.value[i] = d })
  const nextEmpty = otpDigits.value.findIndex(d => !d)
  const focusIndex = nextEmpty === -1 ? 5 : nextEmpty
  nextTick(() => otpInputs.value[focusIndex]?.focus())
}

function startResendCountdown() {
  if (resendTimer) clearInterval(resendTimer)
  resendCountdown.value = 60
  resendTimer = setInterval(() => {
    if (resendCountdown.value > 0) {
      resendCountdown.value--
    }
    else {
      clearInterval(resendTimer!)
      resendTimer = null
    }
  }, 1000)
}

async function onResend() {
  if (isResending.value || resendCountdown.value > 0 || resendMaxed.value) return
  isResending.value = true
  try {
    const result = await userStore.resendTwoFactor(props.challengeId)
    resendCount.value = result.resendCount
    if (result.resendCount >= result.maxResends) resendMaxed.value = true
    toast.add({ title: $t('page.login.twoFactor.resendSuccess'), color: 'success' })
    startResendCountdown()
  }
  catch (e: any) {
    if (e?.response?.status === 403 || e?.status === 403) resendMaxed.value = true
    toast.add({ title: $t('page.login.twoFactor.resendError'), color: 'error' })
  }
  finally {
    isResending.value = false
  }
}

async function submit() {
  if (isLoading.value || code.value.length !== 6) return
  isLoading.value = true
  const { deviceId } = useDeviceId()

  try {
    const result = await userStore.verifyTwoFactor(
      props.challengeId,
      code.value,
      deviceId,
      props.rememberMe,
      props.clientId ?? undefined,
    )

    if (!result) {
      emit('error', $t('page.login.twoFactor.invalidCode'))
      return
    }

    if ('result' in result && result.result === 'account_selection_required') {
      emit('accountSelection', result as MwBffAccountSelectionResponse)
      return
    }

    emit('verified', result as MwBffSessionResponse)
  }
  catch (e: any) {
    const status = e?.response?.status ?? e?.status
    emit('error', status && status >= 400 && status < 500
      ? $t('page.login.twoFactor.invalidCode')
      : $t('general.error.description'))
  }
  finally {
    isLoading.value = false
  }
}

onMounted(() => {
  startResendCountdown()
  nextTick(() => otpInputs.value[0]?.focus())
})

onUnmounted(() => {
  if (resendTimer) clearInterval(resendTimer)
})

defineExpose({ startResendCountdown })
</script>

<template>
  <div class="space-y-6">
    <div class="space-y-3">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
        {{ $t('page.login.twoFactor.codeLabel') }}
      </label>
      <div class="flex gap-2 sm:gap-3 justify-center">
        <input
          v-for="(_, i) in 6"
          :key="i"
          :ref="el => setOtpRef(el, i)"
          :value="otpDigits[i]"
          type="text"
          inputmode="numeric"
          maxlength="1"
          pattern="[0-9]"
          autocomplete="one-time-code"
          :aria-label="`${$t('page.login.twoFactor.codeLabel')} ${i + 1}`"
          class="w-11 h-14 sm:w-12 sm:h-16 text-center text-xl sm:text-2xl font-bold rounded-xl border-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all outline-none focus:ring-2 focus:ring-primary-500/20 caret-transparent select-all"
          :class="otpDigits[i]
            ? 'border-primary-500 dark:border-primary-400 shadow-sm'
            : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400'"
          @input="onOtpInput($event, i)"
          @keydown="onOtpKeydown($event, i)"
          @paste="onOtpPaste($event)"
          @focus="($event.target as HTMLInputElement).select()"
        >
      </div>
    </div>

    <!-- Resend -->
    <div class="text-center text-sm">
      <span v-if="resendMaxed" class="text-red-500 dark:text-red-400">
        {{ $t('page.login.twoFactor.resendLimitReached') }}
      </span>
      <span v-else-if="resendCountdown > 0" class="text-gray-400 dark:text-gray-500">
        {{ $t('page.login.twoFactor.resendIn', { seconds: resendCountdown }) }}
      </span>
      <UButton
        v-else
        variant="link"
        size="sm"
        :loading="isResending"
        class="text-primary-600 dark:text-primary-400 font-medium"
        @click="onResend"
      >
        {{ $t('page.login.twoFactor.resend') }}
      </UButton>
    </div>

    <div class="flex gap-3 pt-2">
      <UButton variant="outline" size="xl" class="flex-1" :disabled="isLoading" @click="emit('back')">
        {{ props.backLabel || $t('general.back') }}
      </UButton>
      <UButton
        size="xl"
        class="flex-1 shadow-lg"
        :loading="isLoading"
        :disabled="code.length !== 6"
        trailing-icon="i-heroicons-arrow-right-20-solid"
        @click="submit"
      >
        {{ $t('page.login.continue') }}
      </UButton>
    </div>
  </div>
</template>
